import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, Skeleton, Container, IconButton, Paper,
  useTheme, useMediaQuery
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EditIcon from '@mui/icons-material/Edit';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { PersonalizedRecommendations, RecentlyViewed } from '../components/Recommendations';
import useBehaviorTracking from '../hooks/useBehaviorTracking';

import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';
const fixImageUrl = (url) => {
  return normalizeImageUrl(url, 'Product');
};

const categoryIconMap = {
  '廚房用品': <RestaurantIcon />,
  '收納整理': <Inventory2Icon />,
  '衛浴清潔': <HomeWorkIcon />,
  '文具辦公': <EditIcon />,
  '節慶活動': <LocalOfferIcon />,
  '居家生活': <HomeWorkIcon />,
  '旅行用品': <LocalShippingIcon />,
  '美妝美容': <FaceRetouchingNaturalIcon />,
  '休閒娛樂': <WhatshotIcon />,
};

const categoryColors = ['#00796b', '#5d4037', '#388e3c', '#f57c00', '#c2185b', '#7b1fa2', '#0288d1', '#689f38'];

const TemplateClassic = () => {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const { addToCart } = useCart();
  const { storeConfig, shippingConfig, categories: dbCategories } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('common');
  useBehaviorTracking();

  const defaultCategoryBlocks = [
    { name: t('template.flash_sale'), icon: <WhatshotIcon />, color: '#e53935', path: '/products?sort=discount' },
    { name: t('template.new_arrivals'), icon: <NewReleasesIcon />, color: '#1976d2', path: '/products?sort=newest' },
  ];

  const categoryBlocks = React.useMemo(() => {
    const dynamicCats = dbCategories.map((cat, idx) => ({
      name: cat.name,
      icon: categoryIconMap[cat.name] || <LocalOfferIcon />,
      color: categoryColors[idx % categoryColors.length],
      path: `/products?category=${encodeURIComponent(cat.name)}`
    }));
    return [...defaultCategoryBlocks, ...dynamicCats];
  }, [dbCategories]);

  const banners = [
    {
      title: storeConfig.store_name || t('misc.my_store'),
      subtitle: storeConfig.slogan || t('template.quality_products'),
      description: t('template.in_stock'),
      bgcolor: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
      bgImage: '/images/banners/banner-classic.jpg',
      tag: t('template.hot_selling')
    },
    {
      title: t('template.great_deals'),
      subtitle: t('template.store_wide_sale'),
      description: t('template.limited_stock'),
      bgcolor: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      tag: t('template.flash_sale')
    },
    {
      title: t('template.free_shipping_promo'),
      subtitle: t('template.free_shipping_full', { threshold: shippingConfig?.free_shipping_threshold || 799 }),
      description: t('template.cvs_payment'),
      bgcolor: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      tag: t('template.limited_time')
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, newRes, hotRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/products?sort=newest&limit=8'),
          api.get('/products?sort=sales&limit=8')
        ]);
        setFeatured(featuredRes.data || []);
        setNewProducts(newRes.data.items || []);
        setHotProducts(hotRes.data.items || []);
      } catch (error) {
        console.error('取得商品失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const calculateDiscount = (original, sale) => {
    if (!original || original <= 0 || !sale) return 0;
    const discount = Math.round((1 - sale / original) * 100);
    return isNaN(discount) || !isFinite(discount) ? 0 : discount;
  };

  const ProductCard = ({ product }) => {
    const discount = calculateDiscount(product.original_price, product.sale_price);
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', bgcolor: 'white', borderRadius: 2, overflow: 'hidden', transition: 'all 0.3s ease', border: '1px solid #eee', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' } }}>
        {discount > 0 && <Box sx={{ position: 'absolute', top: 8, left: 8, bgcolor: '#e53935', color: 'white', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem', fontWeight: 'bold', zIndex: 1 }}>{discount}% OFF</Box>}
        <Box sx={{ position: 'relative', pt: '100%', bgcolor: '#f9f9f9', cursor: 'pointer' }} onClick={() => navigate(`/products/${product.id}`)}>
          <CardMedia component="img" image={fixImageUrl(product.image_url)} alt={product.name} loading="lazy" onError={(e) => onImageErrorSetFallback(e, product.name)} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: 2 }} />
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 40, lineHeight: 1.4, color: '#333', cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} onClick={() => navigate(`/products/${product.id}`)}>{product.name}</Typography>
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="h6" sx={{ color: '#e53935', fontWeight: 'bold', fontSize: '1.25rem' }}>${product.sale_price ? Math.round(Number(product.sale_price)).toLocaleString() : 0}</Typography>
            {product.original_price > product.sale_price && <Typography variant="body2" sx={{ color: '#999', textDecoration: 'line-through', fontSize: '0.8rem' }}>${product.original_price ? Math.round(Number(product.original_price)).toLocaleString() : 0}</Typography>}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Chip icon={<LocalShippingIcon sx={{ fontSize: 14 }} />} label={t('template.fast_delivery')} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#1769ff', '& .MuiChip-icon': { color: '#1769ff' } }} />
            {product.quantity <= 10 && product.quantity > 0 && <Chip label={t('template.remaining_count', { count: product.quantity })} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#f57c00' }} />}
          </Box>
        </CardContent>
        <Box sx={{ p: 2, pt: 0 }}>
          <Button fullWidth variant="contained" startIcon={<ShoppingCartIcon />} onClick={() => addToCart(product.id)} disabled={product.quantity <= 0} sx={{ bgcolor: product.quantity > 0 ? theme.palette.primary.main : '#ccc', '&:hover': { bgcolor: product.quantity > 0 ? theme.palette.primary.dark : '#ccc' }, textTransform: 'none', fontWeight: 'bold' }}>
            {product.quantity > 0 ? t('btn.add_to_cart') : t('template.sold_out')}
          </Button>
        </Box>
      </Card>
    );
  };

  const SectionHeader = ({ icon, title, subtitle, linkTo }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
      {linkTo && <Button onClick={() => navigate(linkTo)} sx={{ color: theme.palette.primary.main }}>{t('btn.view_more')} &gt;</Button>}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Category bar */}
      <Box sx={{ bgcolor: 'white', py: 1.5, borderBottom: '1px solid #eee', overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 2, minWidth: 'max-content' }}>
            {categoryBlocks.map((cat) => (
              <Box key={cat.name} onClick={() => navigate(cat.path)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: 70 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: `${cat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cat.color, '& svg': { fontSize: 24 } }}>{cat.icon}</Box>
                <Typography variant="caption" sx={{ mt: 0.5, color: '#333', fontWeight: 500 }}>{cat.name}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Banner */}
      <Container maxWidth="lg" sx={{ pt: 2 }}>
        <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', transition: 'transform 0.5s ease', transform: `translateX(-${currentBanner * 100}%)` }}>
            {banners.map((banner, idx) => (
              <Box key={idx} sx={{ minWidth: '100%', background: banner.bgcolor, backgroundImage: banner.bgImage ? `url(${banner.bgImage})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', py: { xs: 4, md: 6 }, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: { xs: 180, md: 280 } }}>
                <Chip label={banner.tag} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mb: 2, fontWeight: 'bold' }} />
                <Typography variant={isMobile ? 'h4' : 'h2'} sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>{banner.title}</Typography>
                <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ color: 'white', mt: 1, textAlign: 'center' }}>{banner.subtitle}</Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1, textAlign: 'center' }}>{banner.description}</Typography>
                <Button variant="contained" size="large" onClick={() => navigate('/products')} sx={{ mt: 3, bgcolor: 'white', color: '#333', fontWeight: 'bold', px: 4, '&:hover': { bgcolor: '#f5f5f5' } }}>{t('btn.shop_now')}</Button>
              </Box>
            ))}
          </Box>
          <IconButton onClick={() => setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length)} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}><ArrowBackIosNewIcon /></IconButton>
          <IconButton onClick={() => setCurrentBanner((prev) => (prev + 1) % banners.length)} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}><ArrowForwardIosIcon /></IconButton>
          <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
            {banners.map((_, index) => (<Box key={index} onClick={() => setCurrentBanner(index)} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: currentBanner === index ? 'white' : 'rgba(255,255,255,0.5)', cursor: 'pointer' }} />))}
          </Box>
        </Box>
      </Container>

      {/* Info bar */}
      <Container maxWidth="lg">
        <Box sx={{ bgcolor: 'white', py: 2, my: 2, borderRadius: 2 }}>
          <Grid container spacing={2} justifyContent="center">
            {[
              { icon: <LocalShippingIcon />, text: t('template.free_shipping_threshold', { threshold: shippingConfig?.free_shipping_threshold || 499 }), color: theme.palette.primary.main },
              { icon: <LocalOfferIcon />, text: t('template.great_price'), color: '#4caf50' },
              { icon: <WhatshotIcon />, text: t('template.flash_sale'), color: '#e53935' },
              { icon: <TrendingUpIcon />, text: t('template.selected_goods'), color: '#ff9800' }
            ].map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Box sx={{ color: item.color }}>{item.icon}</Box>
                  <Typography variant="body2" fontWeight="medium">{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        {/* Featured */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader icon={<LocalOfferIcon sx={{ color: '#e53935' }} />} title={t('template.today_discover')} subtitle={t('template.today_discover_sub')} linkTo="/products" />
          <Grid container spacing={2}>
            {loading ? Array(8).fill(0).map((_, i) => (<Grid item xs={6} sm={4} md={3} key={i}><Card sx={{ borderRadius: 2 }}><Skeleton variant="rectangular" sx={{ pt: '100%' }} /><CardContent><Skeleton width="80%" /><Skeleton width="60%" /></CardContent></Card></Grid>))
              : featured.length > 0 ? featured.map(product => (<Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>))
                : <Grid item xs={12}><Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 2 }}><Inventory2Icon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} /><Typography color="text.secondary">{t('template.no_data', '暫無商品資料')}</Typography></Box></Grid>}
          </Grid>
        </Box>

        {/* Hot */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader icon={<WhatshotIcon sx={{ color: '#ff5722' }} />} title={t('template.hot_ranking')} subtitle={t('template.hot_ranking_sub')} linkTo="/products?sort=sales" />
          <Grid container spacing={2}>
            {loading ? Array(4).fill(0).map((_, i) => (<Grid item xs={6} sm={4} md={3} key={`hot-skel-${i}`}><Card sx={{ borderRadius: 2 }}><Skeleton variant="rectangular" sx={{ pt: '100%' }} /><CardContent><Skeleton width="80%" /><Skeleton width="60%" /></CardContent></Card></Grid>))
              : hotProducts.length > 0 ? hotProducts.slice(0, 4).map((product, index) => (
                <Grid item xs={6} sm={4} md={3} key={product.id}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, bgcolor: index < 3 ? '#e53935' : '#666', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.85rem', zIndex: 2 }}>{index + 1}</Box>
                    <ProductCard product={product} />
                  </Box>
                </Grid>
              )) : <Grid item xs={12}><Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 2 }}><Inventory2Icon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} /><Typography color="text.secondary">{t('template.no_data', '暫無商品資料')}</Typography></Box></Grid>}
          </Grid>
        </Box>

        {/* New */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader icon={<NewReleasesIcon sx={{ color: '#1976d2' }} />} title={t('template.new_arrivals')} subtitle={t('template.new_arrivals_sub')} linkTo="/products?sort=newest" />
          <Grid container spacing={2}>
            {loading ? Array(4).fill(0).map((_, i) => (<Grid item xs={6} sm={4} md={3} key={`new-skel-${i}`}><Card sx={{ borderRadius: 2 }}><Skeleton variant="rectangular" sx={{ pt: '100%' }} /><CardContent><Skeleton width="80%" /><Skeleton width="60%" /></CardContent></Card></Grid>))
              : newProducts.length > 0 ? newProducts.slice(0, 4).map(product => (<Grid item xs={6} sm={4} md={3} key={`new-${product.id}`}><ProductCard product={product} /></Grid>))
                : <Grid item xs={12}><Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 2 }}><Inventory2Icon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} /><Typography color="text.secondary">{t('template.no_data', '暫無商品資料')}</Typography></Box></Grid>}
          </Grid>
        </Box>

        <RecentlyViewed limit={8} />
        <PersonalizedRecommendations limit={8} title={t('template.guess_you_like')} />

        {/* Brand guarantee */}
        <Paper sx={{ p: 4, mt: 4, borderRadius: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={4} justifyContent="center">
            {[
              { icon: <SavingsIcon />, title: t('template.great_price'), desc: t('template.save_more'), color: '#4caf50' },
              { icon: <LocalShippingIcon />, title: t('template.fast_delivery'), desc: t('template.fast_delivery_desc'), color: theme.palette.primary.main },
              { icon: <CreditCardIcon />, title: t('template.safe_payment'), desc: t('template.cvs_payment'), color: '#ff9800' },
              { icon: <SupportAgentIcon />, title: t('template.professional_service'), desc: storeConfig.service_hours || t('template.service_hours_default'), color: '#9c27b0' }
            ].map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: item.color, mb: 1, '& svg': { fontSize: 40 } }}>{item.icon}</Box>
                  <Typography variant="subtitle1" fontWeight="bold">{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default TemplateClassic;
